import React, { memo, useEffect, useRef, useState } from 'react'
import {
    Container,
    Button,
    ListGroup,
    ButtonGroup
} from 'react-bootstrap'
import {
    EditorContent,
    useEditor,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Link as RouterLink } from 'react-router-dom';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    List,
    Link2,
    Paperclip,
    Image,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Code,
} from 'react-feather'
import parse from 'html-react-parser';
import { MIME_TYPES } from 'config/constants'
import { useField, useFormikContext } from 'formik'
import { cloneDeep } from 'lodash'
import TextAlign from '@tiptap/extension-text-align'
import { extractUploadFileName, isValidValue } from "helpers/common"
import CharacterCount from '@tiptap/extension-character-count'
import AppFileUploadForPdf from 'components/shared/FormComponents/AppFileUploadForPdf'

export default function CustomEditor({ style, name, attachmentName, previewMode = false, limit = 1500, className }) {
    console.log("attachmentName", attachmentName)
    const { setFieldValue, setFieldTouched, values } = useFormikContext();
    const [field, meta] = useField(name);
    const [attachmentField, attachmentMeta] = useField(attachmentName);
    const [charCount, setCharCount] = useState(0);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                code: false,
                codeBlock: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            CharacterCount.configure({
                limit,
            }),
        ],
        content: isValidValue(field.value) ? field.value : "",
        onUpdate: ({ editor }) => {
            setFieldValue(name, editor.getHTML());
            setCharCount(editor.storage.characterCount.characters());
        },
        onBlur: () => {
            setFieldTouched(name, true);
        }
    });

    useEffect(() => {
        if (editor && field.value !== editor.getHTML()) {
            editor.commands.setContent(isValidValue(field.value) ? field.value : '');
        }
    }, [field.value, editor]);

    const handleFileUpload = (name, value) => {
        let attachmentData = cloneDeep(attachmentField?.value);

        // Ensure it's an array
        if (!Array.isArray(attachmentData)) {
            attachmentData = [];
        }

        attachmentData.push({
            attachment_key: value,
            attachment_name: value,
        });

        setFieldValue(attachmentName, attachmentData);
    };

    const handleRemoveAttachment = (index) => {
        // Remove the attachment link from the editor content
        const attachmentData = cloneDeep(attachmentField)?.value
        const data = attachmentData?.filter((item, i) => i != index)
        setFieldValue(attachmentName, data)
    }

    const addImage = () => {
        const url = window.prompt('Enter the URL of the image:')
        if (url) {
            editor.chain().focus().setImage({ src: url }).run()
        }
    }

    if (!editor) {
        return null
    }

    return (
        <>
            {
                previewMode ? <div className="my-3">{parse(field?.value)}</div> :
                    <>
                        <div style={style} className="border editor rounded-top p-2 bg-light d-flex flex-wrap align-items-center">
                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleBold().run()}
                                    active={editor.isActive('bold')}
                                    size="sm"
                                >
                                    <Bold size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleItalic().run()}
                                    active={editor.isActive('italic')}
                                    size="sm"
                                >
                                    <Italic size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                                    active={editor.isActive('underline')}
                                    size="sm"
                                >
                                    <UnderlineIcon size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                                    active={editor.isActive('bulletList')}
                                    size="sm"
                                >
                                    <List size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                                    active={editor.isActive({ textAlign: 'left' })}
                                    size="sm"
                                >
                                    <AlignLeft size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                                    active={editor.isActive({ textAlign: 'center' })}
                                    size="sm"
                                >
                                    <AlignCenter size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                                    active={editor.isActive({ textAlign: 'right' })}
                                    size="sm"
                                >
                                    <AlignRight size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                                    active={editor.isActive({ textAlign: 'justify' })}
                                    size="sm"
                                >
                                    <AlignJustify size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => {
                                        const previousUrl = editor.getAttributes('link').href
                                        let url = window.prompt('Enter URL')

                                        if (url === null) return

                                        if (url === '') {
                                            editor.chain().focus().extendMarkRange('link').unsetLink().run()
                                            return
                                        }

                                        if (!/^https?:\/\//i.test(url)) {
                                            url = 'https://' + url
                                        }

                                        // ✅ If no text is selected, insert the link as text
                                        if (editor.state.selection.empty) {
                                            editor
                                                .chain()
                                                .focus()
                                                .insertContent(`<a href="${url}" target="_blank">${url}</a>`)
                                                .run()
                                        } else {
                                            editor
                                                .chain()
                                                .focus()
                                                .extendMarkRange('link')
                                                .setLink({ href: url })
                                                .run()
                                        }
                                    }}
                                    active={editor.isActive('link')}
                                    size="sm"
                                >
                                    <Link2 size={16} />
                                </Button>

                                {
                                    attachmentName &&
                                    <div className="">
                                        <AppFileUploadForPdf
                                            // disabled={previewMode}
                                            disableListing={true}
                                            name={attachmentName}
                                            acl={"public-read"}
                                            accept="image/*, application/pdf, .doc, .docx"
                                            allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.webp, MIME_TYPES.doc, MIME_TYPES.docx]}
                                            className={`form-control`}
                                            onChange={handleFileUpload}
                                            role="button"
                                        />
                                    </div>
                                }
                            </ButtonGroup>

                        </div>

                        <div className={`border border-top-0 tip-tap-custom rounded-bottom p-3 ${className ? className : ""}`}>
                            <EditorContent editor={editor} />
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <small className={`text-${charCount >= limit ? "danger" : "muted"}`}>
                                {charCount}/{limit}
                            </small>
                        </div>
                        {meta.touched && meta.error ? (
                            <div className="error">{meta.error}</div>
                        ) : null}
                    </>
            }

            {console.log("attachmentField?.value", attachmentField?.value)}
            {isValidValue(attachmentField?.value) && attachmentField?.value?.length > 0 && (
                <div className="mt-3">
                    <h6 className="text-muted">Attachments</h6>
                    <ListGroup>
                        {attachmentField?.value?.map((file, index) => (
                            <ListGroup.Item key={index} className="d-flex align-items-center">
                                <Paperclip size={16} className="me-2 text-secondary" />
                                <RouterLink target='_blank' to={file?.attachment_name} className="text-truncate" style={{ maxWidth: '300px' }}>
                                    {extractUploadFileName(file?.attachment_name)}
                                </RouterLink>
                                {
                                    !previewMode && <Button
                                        variant=""
                                        size="sm"
                                        className="ms-auto text-danger"
                                        onClick={() => handleRemoveAttachment(index)}
                                    >
                                        Remove
                                    </Button>
                                }
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}
        </>
    )
}